import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, query, where, orderBy, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Card, Button, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, TableFooter,
  Select, MenuItem, FormControl, InputLabel, Grid, Alert, Chip, TextField, Checkbox, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';

function fmtDate(iso) {
  if (!iso) return '–';
  const [y,m,d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[+m-1]} ${+d}, ${y}`;
}

export default function TournamentAdminFinancial() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTid = searchParams.get('tid') || '';
  const [tournaments, setTournaments] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  // regId -> { pool, qf, sf, final, paid }. Kept in its own collection rather
  // than on the registration: a registration is what someone bought, and what
  // they won afterwards is a different fact with a different lifetime.
  const [payouts, setPayouts] = useState({});
  const [savingPayout, setSavingPayout] = useState('');

  useEffect(() => { loadAll(); }, []);

  // Payout rows for whichever tournament is on screen.
  useEffect(() => {
    if (!selectedTid) { setPayouts({}); return; }
    getDocs(query(collection(db, 'tournamentPayouts'), where('tournamentId', '==', selectedTid)))
      .then(snap => {
        const next = {};
        snap.docs.forEach(d => { next[d.id] = d.data(); });
        setPayouts(next);
      })
      .catch(() => setPayouts({}));   // collection may not exist yet
  }, [selectedTid]);

  const savePayout = async (regId, patch) => {
    const current = payouts[regId]
      || { pool: 0, qf: 0, sf: 0, final: 0, undefeatedPool: false, undefeatedOverall: false, paid: false };
    const next = { ...current, ...patch };
    setPayouts(p => ({ ...p, [regId]: next }));      // optimistic; the desk is mid-event
    setSavingPayout(regId);
    try {
      await setDoc(doc(db, 'tournamentPayouts', regId), {
        ...next, tournamentId: selectedTid, updatedAt: new Date().toISOString(),
      }, { merge: true });
    } catch (e) {
      console.error('payout save failed', e);
    }
    setSavingPayout('');
  };
  useEffect(() => { if (tournaments.length > 0) buildSelected(); }, [selectedTid, tournaments, summaries]);

  async function loadAll() {
    try {
      const tSnap = await getDocs(query(collection(db, 'tournaments'), orderBy('date', 'desc')));
      const tList = tSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTournaments(tList);
      // All active registrations — assume all teams are paid in full (some pay onsite in cash)
      const rSnap = await getDocs(query(collection(db, 'registrations'),
        where('status', '==', 'active')));
      const regs = rSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const sums = tList.map(t => {
        const tRegs = regs.filter(r => r.tournament_id === t.id);
        // Tournament prices map: { divisionName: dollars } — may be stored as JSON string
        const prices = typeof t.prices === 'string'
          ? (() => { try { return JSON.parse(t.prices); } catch { return {}; } })()
          : (t.prices || {});
        const tournamentPriceDollars = t.price_dollars || 0;
        // Revenue per team:
        //   1. Use registration's amount_paid (Stripe truth — handles merged-division pricing)
        //   2. Fall back to tournament.prices[division] (for onsite/cash payments)
        //   3. Fall back to tournament.price_dollars
        const revenue = tRegs.reduce((acc, r) => {
          if (r.amount_paid && r.amount_paid > 0) {
            return acc + (r.amount_paid / 100);
          }
          const fee = (prices[r.division] != null) ? Number(prices[r.division]) : tournamentPriceDollars;
          return acc + (isNaN(fee) ? 0 : fee);
        }, 0);
        return { id: t.id, name: t.name, date: t.date, eventType: t.eventType || 'tournament',
          cashCowPayouts: t.cashCowPayouts || null,
          total_teams: tRegs.length, total_revenue: revenue, regs: tRegs, prices, tournamentPriceDollars };
      });
      setSummaries(sums);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  function buildSelected() {
    if (!selectedTid) { setSelected(null); return; }
    const sum = summaries.find(s => s.id === selectedTid);
    if (!sum) return;
    const t = tournaments.find(t => t.id === selectedTid);
    const divs = Array.isArray(t?.divisions) ? t.divisions : JSON.parse(t?.divisions || '[]');
    const divBreakdown = divs.map(div => {
      const divRegs = sum.regs.filter(r => r.division === div);
      const fallbackFee = (sum.prices[div] != null) ? Number(sum.prices[div]) : sum.tournamentPriceDollars;
      const revenue = divRegs.reduce((acc, r) => {
        if (r.amount_paid && r.amount_paid > 0) return acc + (r.amount_paid / 100);
        return acc + (isNaN(fallbackFee) ? 0 : fallbackFee);
      }, 0);
      return { name: div, teams: divRegs.length, revenue };
    }).filter(d => d.teams > 0);
    setSelected({ ...sum, location: t?.location || '', divisions: divBreakdown });
  }

  const grandTotalTeams = summaries.reduce((a, s) => a + s.total_teams, 0);
  const grandTotalRevenue = summaries.reduce((a, s) => a + s.total_revenue, 0);

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Typography variant="h4" fontWeight="bold">📊 Financial Summary</Typography>
        </Container>
      </Box>
      <Container sx={{ pb: 6 }}>

        <Card elevation={2} sx={{ borderRadius: 3, mb: 4, p: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Tournament</InputLabel>
                <Select value={selectedTid} label="Select Tournament"
                        onChange={e => setSearchParams(e.target.value ? { tid: e.target.value } : {})}>
                  <MenuItem value="">— Choose a Tournament —</MenuItem>
                  {tournaments.map(t => <MenuItem key={t.id} value={t.id}>{t.name} ({fmtDate(t.date)})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {selectedTid && (
              <Grid size={{ xs: 12, sm: 2 }}>
                <Button variant="outlined" fullWidth onClick={() => setSearchParams({})}>Clear</Button>
              </Grid>
            )}
          </Grid>
        </Card>

        {selected && (
          <>
            <Card elevation={2} sx={{ borderRadius: 3, mb: 4, borderLeft: '5px solid', borderLeftColor: 'primary.main' }}>
              <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold">{selected.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />{fmtDate(selected.date)}
                    {selected.location && <> &nbsp;<LocationOnIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />{selected.location}</>}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h4" fontWeight="bold" color="success.main">${selected.total_revenue.toFixed(2)}</Typography>
                  <Typography variant="body2" color="text.secondary">{selected.total_teams} team{selected.total_teams !== 1 ? 's' : ''}</Typography>
                </Box>
              </Box>
            </Card>

            {selected.eventType === 'cashcow' && (() => {
              const rates = selected.cashCowPayouts || { pool: 10, qf: 25, sf: 50, final: 150 };
              const bonusPool = Number(rates.undefeatedPool) || 0;
              const bonusAll = Number(rates.undefeatedOverall) || 0;
              // Per-win money, then the two flat bonuses. Whether a team was
              // undefeated is ticked rather than derived: wins are typed in from
              // vb-scores, so nothing here knows how many they lost.
              const owedFor = (w) => (Number(w?.pool) || 0) * (Number(rates.pool) || 0)
                + (Number(w?.qf) || 0) * (Number(rates.qf) || 0)
                + (Number(w?.sf) || 0) * (Number(rates.sf) || 0)
                + (Number(w?.final) || 0) * (Number(rates.final) || 0)
                + (w?.undefeatedPool ? bonusPool : 0)
                + (w?.undefeatedOverall ? bonusAll : 0);
              const rows = selected.regs.map(r => {
                const w = payouts[r.id] || {};
                const name = [r.first_name, r.last_name].filter(Boolean).join(' ')
                  + (r.partner_name || r.partner_first_name
                    ? ` / ${r.partner_name || [r.partner_first_name, r.partner_last_name].filter(Boolean).join(' ')}`
                    : '');
                return { reg: r, w, name: name || '(unnamed team)', owed: owedFor(w) };
              }).sort((a, b) => b.owed - a.owed || a.name.localeCompare(b.name));
              const totalOwed = rows.reduce((t, x) => t + x.owed, 0);
              const totalPaid = rows.filter(x => x.w.paid).reduce((t, x) => t + x.owed, 0);
              const outstanding = totalOwed - totalPaid;
              return (
                <Card elevation={2} sx={{ borderRadius: 3, mb: 4, borderLeft: '5px solid', borderLeftColor: 'warning.main' }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider',
                    display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography fontWeight="bold" sx={{ flexGrow: 1 }}>CashCow payouts</Typography>
                    <Chip size="small" label={`Pool $${rates.pool} · QF $${rates.qf} · SF $${rates.sf} · Final $${rates.final}`
                      + (bonusPool ? ` · undef pool $${bonusPool}` : '')
                      + (bonusAll ? ` · undef overall $${bonusAll}` : '')} />
                  </Box>
                  <Box sx={{ px: 3, py: 1.5, display: 'flex', gap: 4, flexWrap: 'wrap', bgcolor: 'grey.50' }}>
                    {[['Owed', totalOwed, 'text.primary'],
                      ['Paid out', totalPaid, 'success.main'],
                      ['Still to pay', outstanding, outstanding > 0 ? 'warning.main' : 'text.secondary'],
                      ['Revenue less payouts', selected.total_revenue - totalOwed,
                        selected.total_revenue - totalOwed >= 0 ? 'success.main' : 'error.main']].map(([l, v, c]) => (
                      <Box key={l}>
                        <Typography variant="caption" color="text.secondary" display="block">{l}</Typography>
                        <Typography fontWeight="bold" sx={{ color: c }}>${Number(v).toFixed(2)}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 3, pt: 1.5 }}>
                    Matches are run in vb-scores — enter each team's wins here as they come in.
                    Tick Paid when the cash is handed over.
                  </Typography>
                  {/* The card clips its overflow, so on a phone the Payout and
                      Paid columns were simply unreachable — and the desk is
                      exactly where this gets used. Scroll the table, not the page. */}
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ pl: 3 }}>Team</TableCell>
                        <TableCell>Division</TableCell>
                        {['Pool', 'QF', 'SF', 'Final'].map(h =>
                          <TableCell key={h} align="center" sx={{ width: 74 }}>{h}</TableCell>)}
                        {/* Only shown when the event actually offers the bonus. */}
                        {bonusPool > 0 && (
                          <Tooltip title={`Undefeated in pool — $${bonusPool}`}>
                            <TableCell align="center" sx={{ width: 70 }}>Undef&nbsp;pool</TableCell>
                          </Tooltip>
                        )}
                        {bonusAll > 0 && (
                          <Tooltip title={`Undefeated overall — $${bonusAll}`}>
                            <TableCell align="center" sx={{ width: 70 }}>Undef&nbsp;all</TableCell>
                          </Tooltip>
                        )}
                        <TableCell align="right">Payout</TableCell>
                        <TableCell align="center" sx={{ pr: 3 }}>Paid</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(({ reg, w, name, owed }) => (
                        <TableRow key={reg.id} hover sx={{ opacity: savingPayout === reg.id ? 0.6 : 1 }}>
                          <TableCell sx={{ pl: 3 }}>{name}</TableCell>
                          <TableCell><Typography variant="body2" color="text.secondary">{reg.division}</Typography></TableCell>
                          {['pool', 'qf', 'sf', 'final'].map(k => (
                            <TableCell key={k} align="center">
                              <TextField size="small" type="number" value={w[k] ?? ''}
                                onChange={e => savePayout(reg.id, { [k]: Number(e.target.value) || 0 })}
                                inputProps={{ min: 0, style: { textAlign: 'center', padding: '4px 2px', width: 34 } }} />
                            </TableCell>
                          ))}
                          {bonusPool > 0 && (
                            <TableCell align="center">
                              <Checkbox size="small" checked={!!w.undefeatedPool}
                                onChange={e => savePayout(reg.id, { undefeatedPool: e.target.checked })} />
                            </TableCell>
                          )}
                          {bonusAll > 0 && (
                            <TableCell align="center">
                              <Checkbox size="small" checked={!!w.undefeatedOverall}
                                onChange={e => savePayout(reg.id, { undefeatedOverall: e.target.checked })} />
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <Typography fontWeight={owed > 0 ? 700 : 400}>${owed.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ pr: 3 }}>
                            {/* Nothing to hand over until they have won something. */}
                            <Tooltip title={owed > 0 ? 'Cash handed over' : 'Nothing owed yet'}>
                              <span>
                                <Checkbox size="small" checked={!!w.paid} disabled={owed <= 0}
                                  onChange={e => savePayout(reg.id, { paid: e.target.checked })} />
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={6 + (bonusPool > 0 ? 1 : 0) + (bonusAll > 0 ? 1 : 0)}
                          sx={{ pl: 3, fontWeight: 'bold' }}>
                          {rows.filter(r => r.w.paid).length} of {rows.filter(r => r.owed > 0).length} winning teams paid
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>${totalOwed.toFixed(2)}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableFooter>
                  </Table>
                  </Box>
                </Card>
              );
            })()}

            {selected.divisions.length > 0 ? (
              <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography fontWeight="bold">Breakdown by Division</Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ pl: 3 }}>Division</TableCell>
                      <TableCell align="center">Teams</TableCell>
                      <TableCell align="center">Avg Fee</TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>Revenue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selected.divisions.map(div => (
                      <TableRow key={div.name}>
                        <TableCell sx={{ pl: 3, fontWeight: 'bold' }}>{div.name}</TableCell>
                        <TableCell align="center"><Chip label={div.teams} size="small" /></TableCell>
                        <TableCell align="center"><Typography variant="body2" color="text.secondary">${div.teams > 0 ? (div.revenue/div.teams).toFixed(2) : '0.00'}</Typography></TableCell>
                        <TableCell align="right" sx={{ pr: 3, color: 'success.main', fontWeight: 'bold' }}>${div.revenue.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ pl: 3, fontWeight: 'bold' }} colSpan={2}>Tournament Total — {selected.total_teams} teams</TableCell>
                      <TableCell />
                      <TableCell align="right" sx={{ pr: 3, fontWeight: 'bold', color: 'success.main' }}>${selected.total_revenue.toFixed(2)}</TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </Card>
            ) : (
              <Alert severity="info" sx={{ mb: 4 }}>No paid registrations for this tournament yet.</Alert>
            )}
          </>
        )}

        <Card elevation={2} sx={{ borderRadius: 3, mb: 4 }}>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography fontWeight="bold">Running Totals — All Tournaments</Typography>
          </Box>
          {summaries.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a2940' }}>
                  {['Tournament','Date','Teams','Revenue','Running Total'].map(h => (
                    <TableCell key={h} sx={{ color: '#fff', fontWeight: 'bold' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  let running = 0;
                  return summaries.map(s => {
                    running += s.total_revenue;
                    return (
                      <TableRow key={s.id} hover
                        onClick={() => navigate(`/tournament-admin/tournament/${s.id}/financials`)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: selectedTid === s.id ? 'primary.50' : 'inherit',
                          '&:hover': { bgcolor: '#f4f6f9' },
                        }}>
                        <TableCell fontWeight="bold" sx={{ color: 'primary.main' }}>{s.name}</TableCell>
                        <TableCell><Typography variant="body2" color="text.secondary">{fmtDate(s.date)}</Typography></TableCell>
                        <TableCell>{s.total_teams}</TableCell>
                        <TableCell><Typography color="success.main" fontWeight="bold">${s.total_revenue.toFixed(2)}</Typography></TableCell>
                        <TableCell fontWeight="bold">${running.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold' }} colSpan={2}>Grand Total</TableCell>
                  <TableCell fontWeight="bold">{grandTotalTeams}</TableCell>
                  <TableCell><Typography color="success.main" fontWeight="bold">${grandTotalRevenue.toFixed(2)}</Typography></TableCell>
                  <TableCell fontWeight="bold">${grandTotalRevenue.toFixed(2)}</TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography color="text.secondary">No tournament data yet.</Typography>
            </Box>
          )}
        </Card>

        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin')}>
          Back to Dashboard
        </Button>
      </Container>
    </Box>
  );
}
